import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { isEqual } from "lodash";

/**
 * URL-backed state for a table toolbar — search, filters, branch and page.
 *
 * The query string is the single source of truth: there is no mirrored
 * `useState` and therefore no sync effect to get wrong. A filtered view
 * survives a reload, the Back button, and a pasted link.
 *
 * The one exception is the search box, which keeps a local draft and reaches
 * the URL on a trailing debounce — a param write re-renders the page, which a
 * table can't afford per keystroke.
 *
 * Param vocabulary and conventions live in CLAUDE.md ("Table filters in the
 * URL"). Names here must match that table, not the page's local state names.
 */

// ─── Field specs ──────────────────────────────────────────────────────────────

/**
 * How one filter field is validated and encoded. The field's *runtime type* is
 * always the type of its default — the spec only constrains what a URL is
 * allowed to put there, so a hook swap is drop-in at the API call site.
 *
 * - "string"  free text, kept verbatim
 * - "int"     integer-ish; the value stays a string (`roleId: ""` → `"3"`)
 * - "number"  integer-ish parsed to a number (default must be a number)
 * - "bool"    encoded as `1`; absent means false
 * - "date"    `yyyy-MM-dd` string only — never an ISO datetime
 * - "dateObj" same wire format, but the value is a `Date` — for the many filter
 *             panels whose `DatePickerInput` is typed `Date | null`
 * - "csv"     `string[]`, comma-joined
 * - "csvInt"  `string[]` of integer-ish members, comma-joined
 * - {oneOf}   one of a known set, else the default (list APIs 400 on a bad enum)
 */
export type FieldSpec =
  | "string"
  | "int"
  | "number"
  | "bool"
  | "date"
  | "dateObj"
  | "csv"
  | "csvInt"
  | { oneOf: readonly string[] };

/** Sugar for the enum spec: `status: enumOf(Object.values(STATUS))`. */
export const enumOf = (values: readonly string[]): FieldSpec => ({ oneOf: values });

/**
 * Any plain filter-shape object. Deliberately `object` and not
 * `Record<string, unknown>`: the filter types in `types/**` are `interface`s,
 * which have no implicit index signature and so fail that constraint.
 */
export type FilterValues = object;

/** Index a filter object without widening the caller's type. */
const rec = (o: object): Record<string, unknown> => o as Record<string, unknown>;

const INT_RE = /^-?\d+$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** `yyyy-MM-dd` at LOCAL midnight — `new Date("2026-03-01")` is UTC and can land on Feb 28. */
const parseLocalDate = (raw: string): Date | null => {
  const [y, m, d] = raw.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatLocalDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

/** The spec to use when the caller didn't declare one — inferred from the default. */
const inferSpec = (def: unknown): FieldSpec => {
  if (def instanceof Date) return "dateObj";
  if (Array.isArray(def)) return "csv";
  if (typeof def === "number") return "number";
  if (typeof def === "boolean") return "bool";
  return "string";
};

/** The "cleared" value for a field, matching the default's runtime type. */
const emptyFor = (def: unknown): unknown => {
  if (Array.isArray(def)) return [];
  if (typeof def === "number") return 0;
  if (typeof def === "boolean") return false;
  if (def === null) return null;
  return "";
};

const isEmptyValue = (v: unknown): boolean =>
  v === "" || v === null || v === undefined || (Array.isArray(v) && v.length === 0);

/**
 * `null` (param absent) → the default. `""` (param present but empty) → an
 * explicit clear, which is how a field with a non-empty default gets emptied
 * without the default silently coming back.
 */
function decode(raw: string | null, def: unknown, spec: FieldSpec): unknown {
  if (raw === null) return def;
  if (raw === "") return emptyFor(def);

  if (typeof spec === "object") return spec.oneOf.includes(raw) ? raw : def;

  switch (spec) {
    case "int":
      return INT_RE.test(raw) ? raw : def;
    case "number": {
      const n = Number(raw);
      return Number.isFinite(n) ? n : def;
    }
    case "bool":
      return raw === "1" || raw === "true";
    case "date":
      return DATE_RE.test(raw) ? raw : def;
    case "dateObj":
      return DATE_RE.test(raw) ? (parseLocalDate(raw) ?? def) : def;
    case "csv":
      return raw.split(",").map((v) => v.trim()).filter(Boolean);
    case "csvInt":
      return raw.split(",").map((v) => v.trim()).filter((v) => INT_RE.test(v));
    case "string":
    default:
      return raw;
  }
}

/**
 * `null` means "remove the param"; `""` means "write an explicit empty".
 *
 * A value the decoder would reject is never written. Several filter panels use
 * a sentinel for their "All" option (`FilterEnums.Reset === "reset"`) and hand
 * it back as if it were a real value; writing it would put `?status=reset` in
 * the URL and send it to an API that 400s on an unknown enum.
 */
function encode(value: unknown, def: unknown, spec: FieldSpec): string | null {
  if (isEqual(value, def)) return null;
  if (isEmptyValue(value)) return isEmptyValue(def) ? null : "";
  if (spec === "bool") return value ? "1" : null;
  if (value instanceof Date) return formatLocalDate(value);
  if (Array.isArray(value)) {
    const members =
      spec === "csvInt" ? value.filter((v) => INT_RE.test(String(v))) : value;
    return members.length ? members.join(",") : isEmptyValue(def) ? null : "";
  }

  const raw = String(value);
  if (typeof spec === "object") return spec.oneOf.includes(raw) ? raw : null;
  if (spec === "int" && !INT_RE.test(raw)) return null;
  if (spec === "date" && !DATE_RE.test(raw)) return null;
  return raw;
}

// ─── Options / return shape ───────────────────────────────────────────────────

export interface BranchOptions {
  /**
   * Mirror the selection into sessionStorage under this key, so the branch a
   * person picked carries to the next page. The URL still wins on read.
   * Matches the key `useBranchFilter(pageKey)` already writes.
   */
  storageKey?: string;
  /**
   * The value that means "not filtered by branch" — kept out of the URL, the
   * same way a default filter is. Pass `ALL_BRANCHES_VALUE` on any toolbar
   * whose `BranchFilterSelect` has `allowAll`; without it, that select's
   * resolve-on-mount effect stamps `?branchId=all` onto every fresh load.
   * Omit where the select falls back to a real branch instead.
   */
  defaultValue?: string;
}

/**
 * What a clickable stat tile needs: whether it is the view currently on screen,
 * and the click that gets there. Produced by `statFilter`, spread onto a
 * `PageDataStats` item.
 */
export interface StatFilterBinding {
  active: boolean;
  onSelect: () => void;
}

/**
 * What a tile spreads when there is nothing to bind to — for a stats row that
 * also renders on a page with no table, whose component takes `params` as an
 * optional prop. `active: false` and no `onSelect` leaves it a plain read-out.
 */
export const UNBOUND_STAT_TILE: { active: boolean; onSelect?: () => void } = {
  active: false,
  onSelect: undefined,
};

export interface UseTableParamsOptions<F extends FilterValues> {
  /** The unfiltered state. A field equal to its default is absent from the URL. */
  defaults: F;
  /** Per-field validation/encoding. Any field omitted is inferred from its default. */
  schema?: Partial<Record<keyof F, FieldSpec>>;
  /**
   * Param prefix (`emp` → `emp_status`). Set this only when a single route
   * hosts more than one filterable table; plain names everywhere else.
   */
  ns?: string;
  /** Adds a URL-backed `branchId` alongside the filters. */
  branch?: boolean | BranchOptions;
  /** Trailing delay before a typed search term is written to the URL. */
  debounceMs?: number;
  /**
   * Rows per page when the URL says nothing — the size the table asks the API
   * for until the user picks another.
   *
   * `?limit=` is read whether or not this is passed; a table that never reads
   * `pageSize` is simply unaffected by it. Pass it wherever the table's own
   * default is not 10, so the two cannot disagree.
   */
  pageSize?: number;
}

export interface UseTableParams<F extends FilterValues> {
  /** Live search text — includes characters not yet written to the URL. */
  search: string;
  setSearch: (value: string) => void;
  /** Search as committed to the URL. Use this for API args, not `search`. */
  committedSearch: string;

  /** Validated filter values, always complete (defaults filled in). */
  filters: F;
  defaults: F;
  setFilter: <K extends keyof F>(key: K, value: F[K]) => void;
  /** One write for a whole panel — what `FiltersModal.onApply` hands back. */
  applyFilters: (next: F) => void;
  /** Merge a subset of fields in a single write; untouched fields keep theirs. */
  applyPatch: (patch: Partial<F>) => void;
  /**
   * Bind a stat tile to the filter it stands for. Spread the result onto the
   * tile: `{ name, value, icon, ...t.statFilter({ status: "PENDING" }) }`.
   */
  statFilter: (patch: Partial<F>) => StatFilterBinding;

  page: number;
  setPage: (page: number) => void;

  /**
   * Rows per page, from `?limit=`. Pass it as the API's `limit` and to the
   * table's `pagination.pageSize`.
   */
  pageSize: number;
  /** Changing the page size returns to page 1 — page 4 of 10 is not page 4 of 100. */
  setPageSize: (size: number) => void;

  /** `""` until resolved. Always `""` when the `branch` option is off. */
  branchId: string;
  setBranchId: (value: string) => void;

  /** Filter fields differing from their default — the pip on the Filters button. */
  activeCount: number;
  /** Any filter, plus search and branch. */
  hasActive: boolean;
  /**
   * Back to defaults, page 1, search cleared. Leaves foreign params alone.
   *
   * `pageSize` deliberately survives: how many rows someone wants to see is a
   * view preference, not a filter, and clearing a search should not also halve
   * the page they were reading.
   */
  reset: () => void;

  /** Non-default, non-empty filters, ready to spread into a query hook. */
  apiParams: Partial<F>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

const SEARCH = "q";
const PAGE = "page";
const LIMIT = "limit";
const BRANCH = "branchId";

/**
 * Ceiling on a page size read out of the URL.
 *
 * The value reaches the API as `limit`, so an edited query string is a request
 * for however many rows it names. This is not a security boundary — the server
 * owns that — but it keeps a typo or a stale link from asking for 100,000 rows
 * and hanging the table on the response.
 */
const MAX_PAGE_SIZE = 200;

export function useTableParams<F extends FilterValues>({
  defaults,
  schema,
  ns,
  branch = false,
  debounceMs = 300,
  pageSize: defaultPageSize = 10,
}: UseTableParamsOptions<F>): UseTableParams<F> {
  const [searchParams, setSearchParams] = useSearchParams();

  const key = useCallback((name: string) => (ns ? `${ns}_${name}` : name), [ns]);

  const branchStorageKey = branch && branch !== true ? branch.storageKey : undefined;
  const branchDefaultValue = branch && branch !== true ? branch.defaultValue : undefined;
  const branchEnabled = branch !== false;
  const branchOpts = useMemo<BranchOptions | null>(
    () =>
      branchEnabled
        ? { storageKey: branchStorageKey, defaultValue: branchDefaultValue }
        : null,
    [branchEnabled, branchStorageKey, branchDefaultValue],
  );
  const storageKey = branchOpts?.storageKey;

  // `defaults` and `schema` are object literals at nearly every call site, so a
  // fresh identity each render. Freeze the first one — the *shape* is static.
  const defaultsRef = useRef(defaults);
  const schemaRef = useRef(schema);
  const stableDefaults = defaultsRef.current;
  // Must be memoized, not recomputed: it is a dependency of `filters`, and a
  // fresh identity here would give `filters` (and every value derived from it)
  // a new identity on EVERY render, re-rendering the toolbar and the filter
  // panel continuously instead of only when the URL actually changes.
  const defaultsRec = useMemo(() => rec(stableDefaults), [stableDefaults]);
  const specFor = useCallback(
    (name: string): FieldSpec =>
      (schemaRef.current?.[name as keyof F] as FieldSpec | undefined) ??
      inferSpec(rec(defaultsRef.current)[name]),
    [],
  );
  const fieldNames = useMemo(() => Object.keys(stableDefaults), [stableDefaults]);

  // ── Search draft ───────────────────────────────────────────────────────────
  // Typed characters land here first. `null` = nothing pending since the last write.
  const [draftSearch, setDraftSearch] = useState<string | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    [],
  );

  const committedSearch = searchParams.get(key(SEARCH)) ?? "";

  // `write()` (below) navigates via the router, which resolves the URL —
  // and therefore `committedSearch` — one render AFTER the synchronous state
  // update that triggers it. Clearing `draftSearch` inside that same
  // synchronous update used to open a one-render window where `search`
  // (`draftSearch ?? committedSearch`) fell back to the URL's PREVIOUS value,
  // snapping the input backward before the navigation caught up — the
  // flicker/dropped-characters the search box showed while typing. Keeping
  // `draftSearch` authoritative until the URL actually agrees with it closes
  // that window: there is no render where neither reflects the typed value.
  useEffect(() => {
    if (draftSearch !== null && draftSearch === committedSearch) {
      setDraftSearch(null);
    }
  }, [draftSearch, committedSearch]);

  // ── Reads ──────────────────────────────────────────────────────────────────
  const filters = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const name of fieldNames) {
      out[name] = decode(
        searchParams.get(key(name)),
        defaultsRec[name],
        specFor(name),
      );
    }
    return out as F;
  }, [searchParams, key, fieldNames, defaultsRec, specFor]);

  const page = useMemo(() => {
    const n = Number(searchParams.get(key(PAGE)));
    return Number.isInteger(n) && n > 0 ? n : 1;
  }, [searchParams, key]);

  const pageSize = useMemo(() => {
    const n = Number(searchParams.get(key(LIMIT)));
    if (!Number.isInteger(n) || n <= 0) return defaultPageSize;
    return Math.min(n, MAX_PAGE_SIZE);
  }, [searchParams, key, defaultPageSize]);

  const urlBranch = searchParams.get(key(BRANCH));
  const branchId = useMemo(() => {
    if (!branchOpts) return "";
    if (urlBranch !== null) return urlBranch;
    if (storageKey) {
      try {
        const stored = sessionStorage.getItem(storageKey);
        if (stored !== null) return stored;
      } catch {
        // Private mode / storage disabled — the URL alone is enough.
      }
    }
    return branchOpts.defaultValue ?? "";
  }, [branchOpts, urlBranch, storageKey]);

  // ── Write ──────────────────────────────────────────────────────────────────
  /**
   * Build from the LIVE url, not react-router's `prev`: that argument is a
   * snapshot taken when this callback was created. A deferred write — the
   * search debounce — would otherwise navigate back to its stale snapshot and
   * resurrect params removed since, e.g. everything `reset` just deleted.
   *
   * Everything merges, so `hTab`, `vTab`, record ids and any other foreign
   * param are preserved.
   */
  const write = useCallback(
    (
      mutate: (params: URLSearchParams) => void,
      opts: { replace?: boolean; keepPage?: boolean } = {},
    ) => {
      setSearchParams(
        () => {
          const next = new URLSearchParams(window.location.search);
          mutate(next);
          // Any control other than pagination sends the user back to page 1 —
          // folded into the same navigation so there is one refetch, not two.
          if (!opts.keepPage) next.delete(key(PAGE));
          return next;
        },
        { replace: opts.replace ?? true },
      );
    },
    [setSearchParams, key],
  );

  const putFilters = useCallback(
    (params: URLSearchParams, next: Record<string, unknown>) => {
      for (const name of Object.keys(next)) {
        const raw = encode(next[name], defaultsRec[name], specFor(name));
        if (raw === null) params.delete(key(name));
        else params.set(key(name), raw);
      }
    },
    [key, defaultsRec, specFor],
  );

  const setSearch = useCallback(
    (value: string) => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
      const commit = (v: string) => {
        searchTimer.current = null;
        // Left set to `v` (not cleared) — the effect above clears it once
        // `committedSearch` agrees, so `search` never has a render where it
        // falls back to the URL's stale previous value.
        setDraftSearch(v);
        write((p) => {
          if (v.trim()) p.set(key(SEARCH), v);
          else p.delete(key(SEARCH));
        });
      };
      // Emptying the box is a discrete action (a Clear, or the user wiping the
      // field) — apply it at once rather than leaving a write in flight.
      if (!value) {
        commit("");
        return;
      }
      setDraftSearch(value);
      searchTimer.current = setTimeout(() => commit(value), debounceMs);
    },
    [write, key, debounceMs],
  );

  const cancelPendingSearch = useCallback(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = null;
    setDraftSearch(null);
  }, []);

  const setFilter = useCallback(
    <K extends keyof F>(name: K, value: F[K]) => {
      write((p) => putFilters(p, { [name as string]: value }));
    },
    [write, putFilters],
  );

  /**
   * A deliberate Apply from the filter panel pushes a history entry, so Back
   * undoes the filter. Per-keystroke and per-page writes replace instead.
   */
  const applyFilters = useCallback(
    (next: F) => {
      write((p) => putFilters(p, rec(next)), { replace: false });
    },
    [write, putFilters],
  );

  /** Same history semantics as Apply, but only the named fields are touched. */
  const applyPatch = useCallback(
    (patch: Partial<F>) => {
      write((p) => putFilters(p, rec(patch)), { replace: false });
    },
    [write, putFilters],
  );

  /**
   * A stat tile is `active` when every field it stands for already holds the
   * tile's value — so a "Total" tile, whose patch *is* the defaults, lights up
   * exactly when nothing is filtered, with no special-casing at the call site.
   *
   * Clicking an active tile that filters something toggles it back off; the
   * Total tile is inert while it is already the view, rather than pushing a
   * history entry that changes nothing.
   */
  const statFilter = useCallback(
    (patch: Partial<F>): StatFilterBinding => {
      const wanted = rec(patch);
      const names = Object.keys(wanted);
      const current = rec(filters);
      const active = names.every((n) => isEqual(current[n], wanted[n]));
      const isDefault = names.every((n) => isEqual(wanted[n], defaultsRec[n]));
      return {
        active,
        onSelect: () => {
          if (!active) return applyPatch(patch);
          if (isDefault) return;
          applyPatch(
            Object.fromEntries(names.map((n) => [n, defaultsRec[n]])) as Partial<F>,
          );
        },
      };
    },
    [filters, defaultsRec, applyPatch],
  );

  const setPage = useCallback(
    (nextPage: number) => {
      write(
        (p) => {
          if (nextPage > 1) p.set(key(PAGE), String(nextPage));
          else p.delete(key(PAGE));
        },
        { keepPage: true },
      );
    },
    [write, key],
  );

  const setPageSize = useCallback(
    (size: number) => {
      // No `keepPage`: a new page size makes the current page number
      // meaningless, and `write` drops it for us in the same navigation.
      write((p) => {
        if (size === defaultPageSize) p.delete(key(LIMIT));
        else p.set(key(LIMIT), String(size));
      });
    },
    [write, key, defaultPageSize],
  );

  const setBranchId = useCallback(
    (value: string) => {
      if (!branchOpts) return;
      // Guard the write: BranchFilterSelect resolves an empty/stale value from
      // inside a `useEffect`, which would otherwise stamp `?branchId=` onto
      // every page load and push a navigation on mount.
      if (value === branchId) return;
      if (storageKey) {
        try {
          sessionStorage.setItem(storageKey, value);
        } catch {
          // Storage unavailable — the URL still carries the selection.
        }
      }
      write((p) => {
        if (value && value !== branchOpts.defaultValue) p.set(key(BRANCH), value);
        else p.delete(key(BRANCH));
      });
    },
    [branchOpts, branchId, storageKey, write, key],
  );

  const reset = useCallback(() => {
    cancelPendingSearch();
    write(
      (p) => {
        for (const name of fieldNames) p.delete(key(name));
        p.delete(key(SEARCH));
      },
      { replace: false },
    );
  }, [cancelPendingSearch, write, fieldNames, key]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const activeCount = useMemo(
    () => fieldNames.filter((n) => !isEqual(rec(filters)[n], defaultsRec[n])).length,
    [fieldNames, filters, defaultsRec],
  );

  const apiParams = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const name of fieldNames) {
      const value = rec(filters)[name];
      if (isEmptyValue(value)) continue;
      out[name] = value;
    }
    return out as Partial<F>;
  }, [fieldNames, filters]);

  const search = draftSearch ?? committedSearch;
  const hasActive =
    activeCount > 0 ||
    search.trim().length > 0 ||
    (!!branchId && branchId !== branchOpts?.defaultValue);

  // One memoized object: call sites pass the whole thing down as a `params`
  // prop, so a fresh identity every render would defeat every memoized child
  // below it. Every callback in here is already stable.
  return useMemo(
    () => ({
      search,
      setSearch,
      committedSearch,
      filters,
      defaults: stableDefaults,
      setFilter,
      applyFilters,
      applyPatch,
      statFilter,
      page,
      setPage,
      pageSize,
      setPageSize,
      branchId,
      setBranchId,
      activeCount,
      hasActive,
      reset,
      apiParams,
    }),
    [
      search,
      setSearch,
      committedSearch,
      filters,
      stableDefaults,
      setFilter,
      applyFilters,
      applyPatch,
      statFilter,
      page,
      setPage,
      pageSize,
      setPageSize,
      branchId,
      setBranchId,
      activeCount,
      hasActive,
      reset,
      apiParams,
    ],
  );
}

/**
 * Search + page only — for a list page with no filter panel.
 *
 * Pass `ns` where several such tables share one url (tabs on a detail page).
 */
export const useSearchPageParams = (ns?: string, pageSize?: number) =>
  useTableParams<Record<string, never>>({ defaults: {}, ns, pageSize });

/**
 * The shape `useSearchPageParams` returns — for a page that owns the hook and
 * hands it to the table component rendering the rows (see the "hook call lives
 * in a sibling `params.ts`" rule: passing the value down is the other way to
 * keep one call site).
 */
export type SearchPageParams = ReturnType<typeof useSearchPageParams>;

/**
 * One URL-backed value, for a table that only needs a search box or a single
 * dropdown. Omitted from the URL while it equals `fallback`, so an unfiltered
 * view keeps a clean address.
 */
export const useUrlParam = <T extends string = string>(
  // NoInfer so a `""` fallback doesn't narrow T to the literal type.
  name: string,
  fallback: NoInfer<T>,
): [T, (value: T) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = (searchParams.get(name) as T | null) ?? fallback;
  const set = useCallback(
    (next: T) =>
      // Live url for the same reason as `useTableParams`' `write`.
      setSearchParams(
        () => {
          const params = new URLSearchParams(window.location.search);
          if (!next || next === fallback) params.delete(name);
          else params.set(name, next);
          return params;
        },
        { replace: true },
      ),
    [setSearchParams, name, fallback],
  );
  return [value, set];
};
